/** News and catalysts: source-linked inbox, classification evidence, corrections lineage (U051-U053). */
import { decodeCatalystRow, type CatalystRevision, type CatalystRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell, metric } from "../../app/components.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import { formatDurationUs } from "../../app/format.ts";
import { hashOf } from "../../app/router.ts";
import { createTable } from "../../app/table.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { mountOps, panel, sourceLink } from "../ops-common.ts";
import { lineageOf, processingLagUs, receiptLagUs } from "./model.ts";

const link = (row: CatalystRow, label: string): HTMLElement =>
  h("a", { attrs: { href: hashOf("data", "catalysts", row.itemId) } }, label);

function revisionPanel(revision: CatalystRevision, current: boolean): HTMLElement {
  return h(
    "div",
    { class: "revision", attrs: { "data-revision-panel": String(revision.revision) } },
    definitions([
      ["Revision", `${String(revision.revision)}${current ? " (current)" : " (superseded, kept)"}`],
      ["Received", instantCell(revision.receivedAt)],
      ["Classified", instantCell(revision.classifiedAt)],
      ["Classification version", revision.classificationVersion],
      ["Category", revision.category],
      [
        "Materiality score",
        `${String(revision.materialityScore)} of 100 (a score, not a probability)`,
      ],
      ["Note", revision.note],
    ]),
  );
}

function revisions(row: CatalystRow): HTMLElement {
  const shown = h("div");
  const buttons = new Map<number, HTMLElement>();
  const show = (revision: CatalystRevision): void => {
    setChildren(shown, [revisionPanel(revision, revision.revision === row.revision)]);
    for (const [number, button] of buttons) {
      button.setAttribute("aria-pressed", String(number === revision.revision));
    }
  };
  const list = h(
    "ol",
    { class: "revisions", attrs: { "aria-label": "Revisions" } },
    ...row.revisions.map((revision) => {
      const button = h(
        "button",
        {
          attrs: { type: "button", "aria-pressed": "false" },
          on: {
            click: () => {
              show(revision);
            },
          },
        },
        `Show revision ${String(revision.revision)}`,
      );
      buttons.set(revision.revision, button);
      return h("li", {}, button, " received ", instantCell(revision.receivedAt));
    }),
  );
  for (const revision of row.revisions) {
    if (revision.revision === row.revision) {
      show(revision);
    }
  }
  return h("div", {}, list, shown);
}

function lineage(rows: readonly CatalystRow[], row: CatalystRow): HTMLElement {
  const chain = lineageOf(rows, row.itemId);
  const item = (other: CatalystRow, role: string): Child =>
    h("li", { attrs: { "data-lineage": role } }, `${role}: `, link(other, other.headline));
  return h(
    "ol",
    { class: "lineage", attrs: { "aria-label": "Corrections lineage" } },
    ...chain.predecessors.map((other) => item(other, "Earlier")),
    row.supersedes !== null && chain.predecessors.length === 0
      ? h("li", {}, `Earlier item ${row.supersedes} is not in the loaded rows.`)
      : null,
    h("li", { attrs: { "aria-current": "true", "data-lineage": "This item" } }, row.headline),
    ...chain.successors.map((other) => item(other, "Later")),
  );
}

function detail(rows: readonly CatalystRow[], row: CatalystRow): HTMLElement {
  return panel(
    `Evidence: ${row.headline}`,
    "catalyst-detail",
    definitions([
      ["Listing", row.listing],
      ["Source", sourceLink(row.sourceUrl, row.sourceName)],
      ["Event category", row.category],
      ["Novelty", row.novelty],
      [
        "Direction",
        row.leaning === "AMBIGUOUS"
          ? "ambiguous (stated, not resolved)"
          : row.leaning.toLowerCase(),
      ],
      ["Classification version", row.classificationVersion],
      ["Materiality evidence", row.materialityEvidence],
    ]),
    h("h3", {}, "Corrections lineage"),
    lineage(rows, row),
    h("h3", {}, "Every revision is kept"),
    revisions(row),
  );
}

/** Draw the inbox and, for the item named in the route, its evidence, lineage and revisions. */
export function drawCatalysts(rows: readonly CatalystRow[], ctx: ViewContext): readonly Child[] {
  const table = createTable<CatalystRow>({
    label: "Announcement inbox",
    rowKey: (row) => row.itemId,
    columns: [
      { key: "headline", header: "Headline", render: (row) => link(row, row.headline) },
      { key: "listing", header: "Listing", render: (row) => row.listing },
      { key: "category", header: "Category", render: (row) => row.category },
      {
        key: "relation",
        header: "Relation",
        render: (row) => badge(row.relation, row.relation === "ORIGINAL" ? "info" : "warn"),
      },
      { key: "novelty", header: "Novelty", render: (row) => row.novelty },
      {
        key: "score",
        header: "Score (not a probability)",
        render: (row) => `${String(row.materialityScore)} / 100`,
      },
      { key: "published", header: "Published", render: (row) => instantCell(row.publishedAt) },
      { key: "received", header: "Received", render: (row) => instantCell(row.receivedAt) },
      {
        key: "lag",
        header: "Receipt lag",
        render: (row) => metric(formatDurationUs(receiptLagUs(row))),
      },
      { key: "processed", header: "Processed", render: (row) => instantCell(row.processedAt) },
      {
        key: "processing",
        header: "Processing time",
        render: (row) => metric(formatDurationUs(processingLagUs(row))),
      },
      { key: "version", header: "Classifier", render: (row) => row.classificationVersion },
    ],
  });
  table.setRows(rows);
  const wanted = ctx.route.param;
  const selected = rows.find((row) => row.itemId === wanted);
  return [
    h(
      "p",
      { class: "note" },
      "Scores are classifier outputs, not confidence probabilities. Publication, receipt and processing times are shown separately.",
    ),
    panel("Announcement inbox", "catalysts-inbox", table.element),
    selected === undefined ? null : detail(rows, selected),
    wanted !== null && selected === undefined
      ? h(
          "p",
          { attrs: { role: "alert", "data-missing-item": wanted } },
          `Item ${wanted} is not among the loaded rows.`,
        )
      : null,
  ];
}

/** Mount the catalyst inbox. */
export function mountCatalysts(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "catalysts",
    title: "News and catalysts",
    projection: "catalysts",
    row: decodeCatalystRow,
    draw: drawCatalysts,
  });
}
